<template>
  <woot-modal
    :show.sync="show"
    :on-close="onClose"
    :show-close-button="false"
    class="schedule-demo-modal-wrapper"
    size="modal-big"
  >
    <div class="schedule-demo-modal">
      <!-- Header: title + tabs (same row, labels list–style) + close -->
      <div class="modal-header">
        <div class="modal-header-left">
          <h2 class="header-title">
            {{ $t('SCHEDULE_DEMO.TITLE') }}
          </h2>
          <div class="schedule-modal-tabs" role="tablist">
            <button
              type="button"
              role="tab"
              :aria-selected="activeTab === 'kivo'"
              class="tab-button"
              :class="{ active: activeTab === 'kivo' }"
              @click="activeTab = 'kivo'"
            >
              {{ $t('SCHEDULE_DEMO.TAB_KIVO') }}
            </button>
            <button
              type="button"
              role="tab"
              :aria-selected="activeTab === 'manual'"
              class="tab-button"
              :class="{ active: activeTab === 'manual' }"
              @click="activeTab = 'manual'"
            >
              {{ $t('SCHEDULE_DEMO.TAB_MANUAL') }}
            </button>
          </div>
        </div>
        <div class="header-actions">
          <button class="close-btn" @click="handleClose">
            <fluent-icon icon="dismiss" size="20" />
          </button>
        </div>
      </div>

      <div class="modal-tab-panels">
        <!-- Kivo Calendar (unchanged flow; only wrapped in a tab panel) -->
        <div v-show="activeTab === 'kivo'" class="tab-panel tab-panel--kivo">
          <!-- Loading State -->
          <div v-if="isInitializing" class="loading-state">
            <spinner size="large" />
            <p class="loading-text">{{ $t('SCHEDULE_DEMO.LOADING') }}</p>
          </div>

          <!-- No Event Types State -->
          <div v-else-if="eventTypes.length === 0" class="no-events-state">
            <fluent-icon icon="calendar" size="48" class="empty-icon" />
            <p class="empty-text">{{ $t('SCHEDULE_DEMO.NO_EVENT_TYPES') }}</p>
            <div class="empty-actions">
              <woot-button @click="goToProfileSettings">
                {{ $t('SCHEDULE_DEMO.ADD_EVENT_TYPE') }}
              </woot-button>
              <woot-button
                v-if="showSwitchToReminder"
                variant="smooth"
                color-scheme="secondary"
                @click="switchToReminder"
              >
                {{ $t('SCHEDULE_DEMO.SET_REMINDER_INSTEAD') }}
              </woot-button>
            </div>
          </div>

          <!-- BOOKING FORM VIEW - Shows when a slot is selected -->
          <div v-else-if="selectedSlot" class="booking-view">
            <!-- Left: Meeting Summary -->
            <div class="booking-summary-panel">
              <h3 class="summary-event-name">{{ selectedEventType?.name }}</h3>

              <div class="summary-info-card">
                <div class="info-row">
                  <svg
                    xmlns="http://www.w3.org/2000/svg"
                    width="16"
                    height="16"
                    viewBox="0 0 24 24"
                    fill="none"
                    stroke="currentColor"
                    stroke-width="2"
                    stroke-linecap="round"
                    stroke-linejoin="round"
                    class="clock-icon"
                  >
                    <circle cx="12" cy="12" r="10" />
                    <polyline points="12 6 12 12 16 14" />
                  </svg>
                  <span>{{ selectedEventType?.duration }} min</span>
                </div>
              </div>

              <div class="summary-info-card">
                <div class="info-label">Timezone:</div>
                <div class="info-value">
                  {{ selectedTimezone.split('/')[1] || selectedTimezone }}
                </div>
              </div>

              <div class="summary-info-card">
                <div class="info-label">Selected time interval:</div>
                <div class="info-value">
                  ({{ formatTime(selectedSlot) }}-{{
                    formatEndTime(selectedSlot)
                  }})
                </div>
                <div class="info-value">{{ selectedDateDisplayFull }}</div>
              </div>
            </div>

            <!-- Right: Booking Form -->
            <div class="booking-form-panel">
              <div class="form-group">
                <label class="form-label">Subject</label>
                <input
                  v-model="bookingSubject"
                  type="text"
                  class="form-input"
                  placeholder="Enter meeting subject"
                />
              </div>

              <div class="form-group">
                <label class="form-label">Add guest's email</label>
                <input
                  v-model="guestEmail"
                  type="email"
                  :class="['form-input', { 'input-error': guestEmailError }]"
                  placeholder="Add guest's email"
                  @keyup.enter="addGuest"
                  @input="clearGuestEmailError"
                />
                <span v-if="guestEmailError" class="error-message">{{
                  guestEmailError
                }}</span>
                <button class="add-guest-btn" @click="addGuest">
                  Add guests
                </button>
                <div v-if="guests.length > 0" class="guests-list">
                  <div
                    v-for="(guest, idx) in guests"
                    :key="idx"
                    class="guest-tag"
                  >
                    <span>{{ guest }}</span>
                    <button class="remove-guest" @click="removeGuest(idx)">
                      <fluent-icon icon="dismiss" size="12" />
                    </button>
                  </div>
                </div>
              </div>

              <div class="form-actions">
                <woot-button
                  variant="smooth"
                  color-scheme="secondary"
                  @click="clearSlotSelection"
                >
                  Back
                </woot-button>
                <woot-button
                  :is-loading="isBooking"
                  color-scheme="primary"
                  @click="bookDemo"
                >
                  Confirm
                </woot-button>
              </div>
            </div>
          </div>

          <!-- CALENDAR VIEW - Shows when no slot is selected -->
          <div v-else class="schedule-content">
            <!-- LEFT PANEL: Event Types -->
            <div class="event-types-panel">
              <div
                v-for="eventType in eventTypes"
                :key="eventType.id"
                :class="[
                  'event-type-card',
                  { selected: selectedEventType?.id === eventType.id },
                ]"
                @click="selectEventType(eventType)"
              >
                <h4 class="event-name">{{ eventType.name }}</h4>
                <div class="event-duration">
                  <svg
                    xmlns="http://www.w3.org/2000/svg"
                    width="14"
                    height="14"
                    viewBox="0 0 24 24"
                    fill="none"
                    stroke="currentColor"
                    stroke-width="2"
                    stroke-linecap="round"
                    stroke-linejoin="round"
                    class="clock-icon"
                  >
                    <circle cx="12" cy="12" r="10" />
                    <polyline points="12 6 12 12 16 14" />
                  </svg>
                  <span>{{ eventType.duration }} min</span>
                </div>

                <!-- Timezone selector for selected event -->
                <div
                  v-if="selectedEventType?.id === eventType.id"
                  class="timezone-section"
                >
                  <label class="timezone-label">Select Timezone:</label>
                  <select
                    v-model="selectedTimezone"
                    class="timezone-select"
                    @change="fetchAvailabilitySlots"
                  >
                    <option
                      v-for="tz in timezones"
                      :key="tz.value"
                      :value="tz.value"
                    >
                      {{ tz.label }}
                    </option>
                  </select>
                </div>
              </div>
            </div>

            <!-- CENTER PANEL: Calendar -->
            <div class="calendar-panel">
              <div class="calendar-header">
                <button
                  class="nav-btn"
                  :disabled="isPreviousMonthDisabled"
                  @click="previousMonth"
                >
                  <fluent-icon icon="chevron-left" size="20" />
                </button>
                <h3 class="month-title">{{ monthYearDisplay }}</h3>
                <button class="nav-btn" @click="nextMonth">
                  <fluent-icon icon="chevron-right" size="20" />
                </button>
              </div>

              <div class="calendar-grid">
                <div class="weekdays">
                  <span v-for="day in weekdays" :key="day" class="weekday">{{
                    day
                  }}</span>
                </div>
                <div class="days">
                  <div
                    v-for="(day, index) in calendarDays"
                    :key="index"
                    :class="getDayClasses(day)"
                    @click="selectDate(day)"
                  >
                    {{ day.date || '' }}
                  </div>
                </div>
              </div>

              <div v-if="isLoadingSlots" class="loading-slots">
                <spinner size="small" />
                <span>{{ $t('SCHEDULE_DEMO.LOADING_SLOTS') }}</span>
              </div>
            </div>

            <!-- RIGHT PANEL: Time Slots -->
            <div class="slots-panel">
              <div v-if="selectedDate" class="slots-header">
                <h4 class="selected-date-title">{{ selectedDateDisplay }}</h4>
                <div class="time-format-toggle">
                  <button
                    :class="['toggle-btn', { active: timeFormat === '12h' }]"
                    @click="timeFormat = '12h'"
                  >
                    12h
                  </button>
                  <button
                    :class="['toggle-btn', { active: timeFormat === '24h' }]"
                    @click="timeFormat = '24h'"
                  >
                    24h
                  </button>
                </div>
              </div>

              <div v-if="!selectedDate" class="no-date-selected">
                <p>{{ $t('SCHEDULE_DEMO.SELECT_DATE') }}</p>
              </div>

              <div v-else-if="availableSlots.length === 0" class="no-slots">
                <p>{{ $t('SCHEDULE_DEMO.NO_SLOTS') }}</p>
              </div>

              <div v-else class="slots-list">
                <button
                  v-for="slot in availableSlots"
                  :key="slot"
                  :class="['slot-btn', { selected: selectedSlot === slot }]"
                  @click="selectSlot(slot)"
                >
                  {{ formatTime(slot) }}
                </button>
              </div>
            </div>
          </div>
        </div>

        <!-- Manual external meeting (same persistence as Kivo booking: contact additional_attributes) -->
        <div
          v-show="activeTab === 'manual'"
          class="tab-panel tab-panel--manual"
        >
          <div class="manual-meeting-form">
            <div class="form-group">
              <label class="form-label">
                {{ $t('SCHEDULE_DEMO.MANUAL_DATETIME_LABEL') }}
                <span class="required-star">*</span>
              </label>
              <woot-date-time-picker
                :value="manualScheduledAt"
                class="date-time-picker-modal"
                :confirm-text="$t('CAMPAIGN.ADD.FORM.SCHEDULED_AT.CONFIRM')"
                :placeholder="$t('CAMPAIGN.ADD.FORM.SCHEDULED_AT.PLACEHOLDER')"
                @change="onManualDatetimeChange"
              />
            </div>
            <div class="form-group">
              <label class="form-label">
                {{ $t('SCHEDULE_DEMO.MANUAL_SUBJECT_LABEL') }}
                <span class="required-star">*</span>
              </label>
              <input
                v-model="manualSubject"
                type="text"
                class="form-input"
                :placeholder="$t('SCHEDULE_DEMO.MANUAL_SUBJECT_PLACEHOLDER')"
              />
            </div>
            <div class="form-group">
              <label class="form-label">{{
                $t('SCHEDULE_DEMO.MANUAL_DESCRIPTION_LABEL')
              }}</label>
              <textarea
                v-model="manualDescription"
                class="form-textarea"
                rows="3"
                :placeholder="
                  $t('SCHEDULE_DEMO.MANUAL_DESCRIPTION_PLACEHOLDER')
                "
              />
            </div>
            <div class="form-group">
              <label class="form-label">
                {{ $t('SCHEDULE_DEMO.MANUAL_LINK_LABEL') }}
                <span class="required-star">*</span>
              </label>
              <input
                v-model="manualMeetingLink"
                type="url"
                class="form-input"
                :class="{ 'input-error': manualLinkError }"
                :placeholder="$t('SCHEDULE_DEMO.MANUAL_LINK_PLACEHOLDER')"
                @input="manualLinkError = ''"
              />
              <span v-if="manualLinkError" class="error-message">{{
                manualLinkError
              }}</span>
            </div>
            <div class="form-group manual-labels-field">
              <label class="form-label">{{
                $t('SCHEDULE_DEMO.MANUAL_LABELS_LABEL')
              }}</label>
              <div class="multiselect-wrap--medium">
                <multiselect
                  v-model="manualSelectedLabels"
                  class="no-margin"
                  :options="allLabels"
                  :multiple="true"
                  :searchable="true"
                  track-by="id"
                  label="title"
                  :close-on-select="false"
                  :show-labels="false"
                  :placeholder="$t('SCHEDULE_DEMO.MANUAL_LABELS_PLACEHOLDER')"
                  :allow-empty="true"
                  open-direction="bottom"
                />
              </div>
            </div>
            <div class="manual-form-actions">
              <woot-button
                variant="smooth"
                color-scheme="secondary"
                @click="handleClose"
              >
                {{ $t('MERGE_CONTACTS.FORM.CANCEL') }}
              </woot-button>
              <woot-button
                color-scheme="primary"
                :is-loading="isSavingManual"
                @click="saveManualMeeting"
              >
                {{ $t('SCHEDULE_DEMO.MANUAL_SAVE') }}
              </woot-button>
            </div>
          </div>
        </div>
      </div>
    </div>
  </woot-modal>
</template>

<script>
import { mapGetters } from 'vuex';
import Spinner from 'shared/components/Spinner.vue';
import alertMixin from 'shared/mixins/alertMixin';
import userEventTypesAPI from 'dashboard/api/userEventTypes';
import WootDateTimePicker from 'dashboard/components/ui/DateTimePicker.vue';

export default {
  components: {
    Spinner,
    WootDateTimePicker,
  },
  mixins: [alertMixin],
  props: {
    show: {
      type: Boolean,
      default: false,
    },
    contact: {
      type: Object,
      required: true,
    },
    showSwitchToReminder: {
      type: Boolean,
      default: true,
    },
  },
  data() {
    return {
      // Start on Kivo while loading; avoids External → Kivo tab flash when event types exist
      activeTab: 'kivo',
      manualScheduledAt: null,
      manualSubject: '',
      manualDescription: '',
      manualMeetingLink: '',
      manualLinkError: '',
      manualSelectedLabels: [],
      isSavingManual: false,
      eventTypes: [],
      selectedEventType: null,
      selectedTimezone: 'Asia/Kolkata',
      currentMonth: new Date().getMonth() + 1,
      currentYear: new Date().getFullYear(),
      selectedDate: null,
      availableSlots: [],
      selectedSlot: null,
      isInitializing: true,
      isLoadingSlots: false,
      isBooking: false,
      timeFormat: '12h',
      availabilityData: {},
      weekdays: ['SU', 'MO', 'TU', 'WE', 'TH', 'FR', 'SA'],
      // Booking form fields
      bookingSubject: '',
      guestEmail: '',
      guests: [],
      guestEmailError: '',
      timezones: [
        { value: 'Asia/Kolkata', label: 'Asia/Kolkata (GMT+05:30)' },
        { value: 'America/New_York', label: 'America/New_York (GMT-05:00)' },
        { value: 'Asia/Bangkok', label: 'Asia/Bangkok (GMT+07:00)' },
        { value: 'Asia/Hong_Kong', label: 'Asia/Hong_Kong (GMT+08:00)' },
        { value: 'Asia/Jakarta', label: 'Asia/Jakarta (GMT+07:00)' },
        { value: 'Asia/Seoul', label: 'Asia/Seoul (GMT+09:00)' },
        { value: 'Asia/Shanghai', label: 'Asia/Shanghai (GMT+08:00)' },
        { value: 'Asia/Singapore', label: 'Asia/Singapore (GMT+08:00)' },
        { value: 'Asia/Tokyo', label: 'Asia/Tokyo (GMT+09:00)' },
      ],
    };
  },
  computed: {
    ...mapGetters({
      currentUser: 'getCurrentUser',
      allLabels: 'labels/getLabels',
    }),

    monthYearDisplay() {
      const date = new Date(this.currentYear, this.currentMonth - 1);
      return date.toLocaleDateString('en-US', {
        month: 'long',
        year: 'numeric',
      });
    },

    selectedDateDisplay() {
      if (!this.selectedDate) return '';
      const date = new Date(
        this.currentYear,
        this.currentMonth - 1,
        this.selectedDate
      );
      return date.toLocaleDateString('en-US', {
        weekday: 'short',
        month: 'short',
        day: 'numeric',
      });
    },

    selectedDateDisplayFull() {
      if (!this.selectedDate) return '';
      const date = new Date(
        this.currentYear,
        this.currentMonth - 1,
        this.selectedDate
      );
      return date.toLocaleDateString('en-US', {
        weekday: 'long',
        month: 'long',
        day: 'numeric',
        year: 'numeric',
      });
    },

    calendarDays() {
      const firstDay = new Date(
        this.currentYear,
        this.currentMonth - 1,
        1
      ).getDay();
      const daysInMonth = new Date(
        this.currentYear,
        this.currentMonth,
        0
      ).getDate();
      const days = [];

      for (let i = 0; i < firstDay; i++) {
        days.push({ date: null, available: false });
      }

      for (let d = 1; d <= daysInMonth; d++) {
        const dateKey = this.getDateKey(d);
        const slots = this.availabilityData[dateKey];
        const hasSlots = Array.isArray(slots) && slots.length > 0;
        days.push({
          date: d,
          available: hasSlots,
          isToday: this.isToday(d),
          isPast: this.isPast(d),
        });
      }

      return days;
    },

    isPreviousMonthDisabled() {
      const today = new Date();
      return (
        this.currentYear === today.getFullYear() &&
        this.currentMonth === today.getMonth() + 1
      );
    },
  },

  watch: {
    show(newVal) {
      if (newVal) {
        this.resetManualMeetingForm();
        this.initialize();
        if (this.activeTab === 'manual') {
          this.ensureManualMeetingLabelsOptions();
        }
      } else {
        this.resetState();
      }
    },
    activeTab(val, oldVal) {
      if (val === 'manual' && this.show) {
        this.ensureManualMeetingLabelsOptions();
      }
      // Email is only required for Kivo booking — warn when user opens Kivo from External
      if (
        val === 'kivo' &&
        oldVal === 'manual' &&
        this.show &&
        !this.contact?.email &&
        this.eventTypes.length > 0
      ) {
        this.showAlert(this.$t('SCHEDULE_DEMO.CONTACT_EMAIL_REQUIRED'));
      }
    },
  },

  mounted() {
    if (this.show) {
      this.resetManualMeetingForm();
      this.initialize();
    }
  },

  methods: {
    async initialize() {
      this.isInitializing = true;
      this.eventTypes = [];
      // Set before any await so the active tab matches Kivo loading UI (no flash to External first)
      this.activeTab = 'kivo';

      try {
        const eventTypesPromise = this.fetchEventTypes();
        const timeoutPromise = new Promise(resolve => {
          setTimeout(() => resolve([]), 8000);
        });

        this.eventTypes = await Promise.race([
          eventTypesPromise,
          timeoutPromise,
        ]);

        // Auto-select the first event type
        if (this.eventTypes.length > 0) {
          await this.selectEventType(this.eventTypes[0]);
        }
      } catch (error) {
        console.error('Error initializing:', error);
        this.eventTypes = [];
      } finally {
        this.isInitializing = false;
        // External meeting only when there are no Kivo event types
        if (this.eventTypes.length === 0) {
          this.activeTab = 'manual';
        }
      }
    },

    async fetchEventTypes() {
      try {
        const response = await userEventTypesAPI.getAll();
        return Array.isArray(response.data) ? response.data : [];
      } catch (error) {
        console.error('Error fetching event types:', error);
        return [];
      }
    },

    async selectEventType(eventType) {
      this.selectedEventType = eventType;
      this.selectedDate = null;
      this.selectedSlot = null;
      this.availableSlots = [];
      this.availabilityData = {};
      // Reset to current month when changing event type
      this.currentMonth = new Date().getMonth() + 1;
      this.currentYear = new Date().getFullYear();
      await this.fetchAvailabilitySlots();
      // Auto-select today if available, otherwise first available day
      this.autoSelectFirstAvailableDate();
    },

    async fetchAvailabilitySlots() {
      if (!this.selectedEventType) return;

      if (!this.contact.email) {
        this.availabilityData = {};
        if (this.activeTab === 'kivo') {
          this.showAlert(this.$t('SCHEDULE_DEMO.CONTACT_EMAIL_REQUIRED'));
        }
        return;
      }

      this.isLoadingSlots = true;
      try {
        const response = await userEventTypesAPI.getAvailabilitySlots({
          eventId: this.selectedEventType.event_id,
          scheduleId: this.selectedEventType.schedule_id,
          month: this.currentMonth,
          year: this.currentYear,
          timeZone: this.selectedTimezone,
          contactEmail: this.contact.email,
        });

        if (response.data?.success) {
          const data = response.data.data;
          this.availabilityData = data && typeof data === 'object' ? data : {};
        } else {
          this.availabilityData = {};
        }
      } catch (error) {
        console.error('Error fetching availability:', error);
        this.availabilityData = {};
      } finally {
        this.isLoadingSlots = false;
      }
    },

    selectDate(day) {
      if (!day.date || day.isPast || !day.available) return;

      this.selectedDate = day.date;
      this.selectedSlot = null;

      const dateKey = this.getDateKey(day.date);
      this.availableSlots = this.availabilityData[dateKey] || [];
    },

    selectSlot(slot) {
      this.selectedSlot = slot;
      // Pre-fill subject with default value
      this.bookingSubject = `Demo with ${this.contact.name || 'Guest'}`;
    },

    clearSlotSelection() {
      this.selectedSlot = null;
      this.bookingSubject = '';
      this.guestEmail = '';
      this.guests = [];
      this.guestEmailError = '';
    },

    addGuest() {
      this.guestEmailError = '';

      if (!this.guestEmail || this.guestEmail.trim() === '') {
        this.guestEmailError = 'Please enter an email address';
        return;
      }

      const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
      if (!emailRegex.test(this.guestEmail)) {
        this.guestEmailError = 'Please enter a valid email address';
        return;
      }

      if (this.guests.includes(this.guestEmail)) {
        this.guestEmailError = 'This email has already been added';
        return;
      }

      this.guests.push(this.guestEmail);
      this.guestEmail = '';
    },

    removeGuest(index) {
      this.guests.splice(index, 1);
    },

    clearGuestEmailError() {
      this.guestEmailError = '';
    },

    resetManualMeetingForm() {
      this.manualScheduledAt = new Date();
      this.manualSubject = '';
      this.manualDescription = '';
      this.manualMeetingLink = '';
      this.manualLinkError = '';
      this.manualSelectedLabels = [];
    },

    async ensureManualMeetingLabelsOptions() {
      try {
        if (!this.allLabels?.length) {
          await this.$store.dispatch('labels/get');
        }
        await this.$store.dispatch('contactLabels/get', this.contact.id);
      } catch (e) {
        // Labels are optional; ignore load failures
      }
    },

    onManualDatetimeChange(value) {
      this.manualScheduledAt = value;
    },

    buildCalendarTimingsFromDate(date) {
      if (!date || Number.isNaN(new Date(date).getTime())) return '';
      const d = new Date(date);
      const dateString = d.toString();
      const dateParts = dateString.split(' ');
      if (dateParts.length < 6 || !dateParts[4] || !dateParts[5]) return '';
      const timeParts = dateParts[4].split(':');
      if (timeParts.length < 3) return '';
      let hour = parseInt(timeParts[0], 10);
      const minute = parseInt(timeParts[1], 10);
      const second = parseInt(timeParts[2], 10);
      const dayName = dateParts[0];
      const monthName = dateParts[1];
      const day = dateParts[2];
      const year = dateParts[3];
      const tz = dateParts[5];
      if (tz.length < 8) return '';
      const gmtOffset = tz.substring(3, 8);
      const ampm = hour >= 12 ? 'PM' : 'AM';
      hour %= 12;
      hour = hour || 12;
      return `${dayName}, ${monthName} ${day}, ${year}, ${hour}:${String(
        minute
      ).padStart(2, '0')}:${second} ${ampm} ${gmtOffset}`;
    },

    manualMeetingLinkValid(link) {
      const trimmed = (link || '').trim();
      if (!trimmed) return false;
      const urlPattern = /^(https?:\/\/)?([\w-]+(\.[\w-]+)+)([\w.,@?^=%&:/~+#-]*[\w@?^=%&/~+#-])?$/;
      return urlPattern.test(trimmed);
    },

    async saveManualMeeting() {
      const subject = (this.manualSubject || '').trim();
      const meetLink = (this.manualMeetingLink || '').trim();
      if (!this.manualScheduledAt || !subject) {
        this.showAlert(this.$t('SCHEDULE_DEMO.MANUAL_VALIDATION'));
        return;
      }
      if (!meetLink) {
        this.manualLinkError = this.$t('SCHEDULE_DEMO.MANUAL_LINK_REQUIRED');
        this.showAlert(this.$t('SCHEDULE_DEMO.MANUAL_LINK_REQUIRED'));
        return;
      }
      if (!this.manualMeetingLinkValid(meetLink)) {
        this.manualLinkError = this.$t('SCHEDULE_DEMO.MANUAL_INVALID_LINK');
        this.showAlert(this.$t('SCHEDULE_DEMO.MANUAL_INVALID_LINK'));
        return;
      }
      this.manualLinkError = '';

      const calendarTimings = this.buildCalendarTimingsFromDate(
        this.manualScheduledAt
      );
      if (!calendarTimings) {
        this.showAlert(this.$t('SCHEDULE_DEMO.MANUAL_VALIDATION'));
        return;
      }
      const description = (this.manualDescription || '').trim();

      this.isSavingManual = true;
      try {
        const response = await this.$store.dispatch(
          'contacts/save_demo_meeting',
          {
            contactId: this.contact.id,
            data: {
              demo_timing: calendarTimings,
              meeting_link: meetLink,
              demo_subject: subject,
              meeting_description: description,
              manual_external_booking: true,
            },
          }
        );

        if (response?.success) {
          if (!this.contact.additional_attributes) {
            this.$set(this.contact, 'additional_attributes', {});
          }
          this.$set(
            this.contact.additional_attributes,
            'demo_timing',
            calendarTimings
          );
          this.$set(
            this.contact.additional_attributes,
            'meeting_link',
            meetLink || null
          );
          this.$set(
            this.contact.additional_attributes,
            'demo_subject',
            subject
          );
          if (description) {
            this.$set(
              this.contact.additional_attributes,
              'meeting_description',
              description
            );
          } else {
            this.$delete(
              this.contact.additional_attributes,
              'meeting_description'
            );
          }
          this.$delete(this.contact.additional_attributes, 'reschedule_url');
          this.$delete(this.contact.additional_attributes, 'rescheduleUrl');
          this.$delete(
            this.contact.additional_attributes,
            'meeting_duration_minutes'
          );

          if (typeof bus !== 'undefined' && bus && bus.$emit) {
            bus.$emit('demo-meeting-updated', {
              contactId: this.contact.id,
              demo_timing: calendarTimings,
              meeting_link: meetLink,
              demo_subject: subject,
              meeting_description: description || null,
            });
          }

          const labelTitles = (this.manualSelectedLabels || [])
            .map(l => l && l.title)
            .filter(Boolean);
          if (labelTitles.length) {
            try {
              const existing =
                this.$store.getters['contactLabels/getContactLabels'](
                  this.contact.id
                ) || [];
              const merged = [...new Set([...existing, ...labelTitles])];
              await this.$store.dispatch('contactLabels/update', {
                contactId: this.contact.id,
                labels: merged,
              });
            } catch (labelErr) {
              console.error('Error assigning labels:', labelErr);
              this.showAlert(this.$t('CONTACT_PANEL.LABELS.CONTACT.ERROR'));
            }
          }

          const startDate = new Date(this.manualScheduledAt);
          const optionsDate = {
            day: '2-digit',
            month: 'short',
            year: 'numeric',
          };
          const optionsTime = {
            hour: '2-digit',
            minute: '2-digit',
            hour12: true,
          };
          this.$emit('demo-scheduled', {
            meetLink,
            scheduledAt: startDate,
            formattedDate: startDate.toLocaleDateString('en-US', optionsDate),
            formattedTime: startDate.toLocaleTimeString('en-US', optionsTime),
            eventType: null,
            demoTiming: calendarTimings,
            manual: true,
          });

          this.showAlert(this.$t('SCHEDULE_DEMO.MANUAL_SUCCESS'));
          this.onClose();
        } else {
          this.showAlert(
            response?.error || this.$t('SCHEDULE_DEMO.MANUAL_ERROR')
          );
        }
      } catch (error) {
        console.error('Error saving manual meeting:', error);
        this.showAlert(
          error.response?.data?.error || this.$t('SCHEDULE_DEMO.MANUAL_ERROR')
        );
      } finally {
        this.isSavingManual = false;
      }
    },

    formatEndTime(slot) {
      if (!slot || !this.selectedEventType) return '';
      const [hours, minutes] = slot.split(':');
      const startDate = new Date();
      startDate.setHours(parseInt(hours, 10), parseInt(minutes, 10), 0, 0);
      const endDate = new Date(
        startDate.getTime() +
          (this.selectedEventType.duration || 15) * 60 * 1000
      );
      const h = endDate.getHours();
      const m = endDate
        .getMinutes()
        .toString()
        .padStart(2, '0');

      if (this.timeFormat === '24h') {
        return `${h.toString().padStart(2, '0')}:${m}`;
      }
      const period = h >= 12 ? 'PM' : 'AM';
      const hour12 = h % 12 || 12;
      return `${hour12}:${m} ${period}`;
    },

    async bookDemo() {
      if (!this.selectedSlot || !this.selectedEventType) return;

      if (!this.contact.email) {
        if (this.activeTab === 'kivo') {
          this.showAlert(this.$t('SCHEDULE_DEMO.CONTACT_EMAIL_REQUIRED'));
        }
        return;
      }

      this.isBooking = true;
      try {
        const [hours, minutes] = this.selectedSlot.split(':');
        const startDate = new Date(
          this.currentYear,
          this.currentMonth - 1,
          this.selectedDate,
          parseInt(hours, 10),
          parseInt(minutes, 10)
        );

        const durationInMs =
          (this.selectedEventType.duration || 15) * 60 * 1000;
        const endDate = new Date(startDate.getTime() + durationInMs);

        const calendarTimings = this.buildCalendarTimingsFromDate(startDate);

        const appointmentData = {
          scheduleType: 'demo',
          calendar_timings: calendarTimings,
          meetingLink: '',
          guests: this.guests,
          userEmail: this.contact.email,
          username: this.contact.name || 'Guest',
          location: 'CONFERENCE_ROOM',
          subject:
            this.bookingSubject || `Demo with ${this.contact.name || 'Guest'}`,
          endTime: endDate.getTime(),
          timeZone: this.selectedTimezone,
          startTime: startDate.getTime(),
          eventTypeId: this.selectedEventType.event_id,
          title: this.selectedEventType.name || 'Demo',
          status: 'confirmed',
          contact_id: this.contact.id,
        };

        const response = await userEventTypesAPI.bookAppointment(
          appointmentData
        );

        if (response.data?.success) {
          const meetLink =
            response.data?.data?.calendarEventData?.hangoutLink ||
            response.data?.data?.meetingLink ||
            response.data?.data?.meetLink ||
            '';

          const optionsDate = {
            day: '2-digit',
            month: 'short',
            year: 'numeric',
          };
          const optionsTime = {
            hour: '2-digit',
            minute: '2-digit',
            hour12: true,
          };
          const formattedDate = startDate.toLocaleDateString(
            'en-US',
            optionsDate
          );
          const formattedTime = startDate.toLocaleTimeString(
            'en-US',
            optionsTime
          );

          // Save demo_timing, meeting_link and subject to contact's additional_attributes
          const demoSubject =
            this.bookingSubject || `Demo with ${this.contact.name || 'Guest'}`;
          try {
            await this.$store.dispatch('contacts/save_demo_meeting', {
              contactId: this.contact.id,
              data: {
                demo_timing: calendarTimings,
                meeting_link: meetLink,
                demo_subject: demoSubject,
                meeting_description: '',
                event_id: this.selectedEventType?.event_id || undefined,
              },
            });
            // Also update the local contact object for immediate UI update
            if (!this.contact.additional_attributes) {
              this.$set(this.contact, 'additional_attributes', {});
            }
            this.$set(
              this.contact.additional_attributes,
              'demo_timing',
              calendarTimings
            );
            this.$set(
              this.contact.additional_attributes,
              'meeting_link',
              meetLink
            );
            this.$set(
              this.contact.additional_attributes,
              'demo_subject',
              demoSubject
            );
            if (this.selectedEventType?.event_id) {
              this.$set(
                this.contact.additional_attributes,
                'event_id',
                this.selectedEventType.event_id
              );
            }
            this.$delete(
              this.contact.additional_attributes,
              'meeting_description'
            );
            if (typeof bus !== 'undefined' && bus && bus.$emit) {
              bus.$emit('demo-meeting-updated', {
                contactId: this.contact.id,
                demo_timing: calendarTimings,
                meeting_link: meetLink,
                demo_subject: demoSubject,
                meeting_description: null,
              });
            }
          } catch (saveError) {
            console.error('Error saving demo meeting data:', saveError);
          }

          this.$emit('demo-scheduled', {
            meetLink,
            scheduledAt: startDate,
            formattedDate,
            formattedTime,
            eventType: this.selectedEventType.name,
            demoTiming: calendarTimings,
          });

          this.showAlert(this.$t('SCHEDULE_DEMO.SUCCESS'));
          this.onClose();
        } else {
          this.showAlert(
            response.data?.error || this.$t('SCHEDULE_DEMO.ERROR')
          );
        }
      } catch (error) {
        console.error('Error booking demo:', error);
        this.showAlert(
          error.response?.data?.error || this.$t('SCHEDULE_DEMO.ERROR')
        );
      } finally {
        this.isBooking = false;
      }
    },

    async previousMonth() {
      if (this.currentMonth === 1) {
        this.currentMonth = 12;
        this.currentYear--;
      } else {
        this.currentMonth--;
      }
      this.selectedDate = null;
      this.selectedSlot = null;
      this.availableSlots = [];
      await this.fetchAvailabilitySlots();
      this.autoSelectFirstAvailableDate();
    },

    async nextMonth() {
      if (this.currentMonth === 12) {
        this.currentMonth = 1;
        this.currentYear++;
      } else {
        this.currentMonth++;
      }
      this.selectedDate = null;
      this.selectedSlot = null;
      this.availableSlots = [];
      await this.fetchAvailabilitySlots();
      this.autoSelectFirstAvailableDate();
    },

    autoSelectFirstAvailableDate() {
      const today = new Date();
      const isCurrentMonth =
        this.currentYear === today.getFullYear() &&
        this.currentMonth === today.getMonth() + 1;

      // Try to select today first (if current month and today has slots)
      if (isCurrentMonth) {
        const todayKey = this.getDateKey(today.getDate());
        const todaySlots = this.availabilityData[todayKey];
        if (Array.isArray(todaySlots) && todaySlots.length > 0) {
          this.selectedDate = today.getDate();
          this.availableSlots = todaySlots;
          return;
        }
      }

      // Otherwise find the first available day in the month
      const daysInMonth = new Date(
        this.currentYear,
        this.currentMonth,
        0
      ).getDate();
      const startDay = isCurrentMonth ? today.getDate() : 1;

      for (let d = startDay; d <= daysInMonth; d++) {
        const dateKey = this.getDateKey(d);
        const slots = this.availabilityData[dateKey];
        if (Array.isArray(slots) && slots.length > 0) {
          this.selectedDate = d;
          this.availableSlots = slots;
          return;
        }
      }
    },

    formatTime(slot) {
      if (!slot) return '';
      const [hours, minutes] = slot.split(':');
      const h = parseInt(hours, 10);
      const m = minutes || '00';

      if (this.timeFormat === '24h') {
        return `${hours}:${m}`;
      }

      const period = h >= 12 ? 'PM' : 'AM';
      const h12 = h % 12 || 12;
      return `${h12}:${m} ${period}`;
    },

    getDateKey(day) {
      return `${this.currentYear}-${String(this.currentMonth).padStart(
        2,
        '0'
      )}-${String(day).padStart(2, '0')}`;
    },

    isToday(day) {
      const today = new Date();
      return (
        day === today.getDate() &&
        this.currentMonth === today.getMonth() + 1 &&
        this.currentYear === today.getFullYear()
      );
    },

    isPast(day) {
      const date = new Date(this.currentYear, this.currentMonth - 1, day);
      const today = new Date();
      today.setHours(0, 0, 0, 0);
      return date < today;
    },

    getDayClasses(day) {
      return {
        'calendar-day': true,
        empty: !day.date,
        today: day.isToday,
        past: day.isPast,
        available: day.available && !day.isPast,
        selected: day.date === this.selectedDate,
      };
    },

    goToProfileSettings() {
      this.$router.push({
        name: 'profile_settings_index',
        query: { focus: 'event-types' },
      });
      this.onClose();
    },

    switchToReminder() {
      this.$emit('switch-to-reminder');
    },

    handleClose() {
      this.onClose();
    },

    resetState() {
      this.resetManualMeetingForm();
      this.selectedEventType = null;
      this.selectedDate = null;
      this.selectedSlot = null;
      this.availableSlots = [];
      this.availabilityData = {};
      this.currentMonth = new Date().getMonth() + 1;
      this.currentYear = new Date().getFullYear();
      this.bookingSubject = '';
      this.guestEmail = '';
      this.guests = [];
      this.guestEmailError = '';
    },

    onClose() {
      this.$emit('close');
    },
  },
};
</script>

<style lang="scss">
// Global styles to ensure modal appears above everything else
.schedule-demo-modal-wrapper.modal-mask,
.modal-mask.schedule-demo-modal-wrapper {
  z-index: 99999 !important;
  position: fixed !important;
  top: 0 !important;
  left: 0 !important;
  right: 0 !important;
  bottom: 0 !important;
  width: 100% !important;
  height: 100% !important;
}

.schedule-demo-modal-wrapper .modal-container {
  width: 95% !important;
  max-width: 950px !important;
  max-height: 520px !important;
  height: 520px !important;
  overflow: hidden !important;
  z-index: 100000 !important;
  border-radius: 16px !important;
}
</style>

<style lang="scss" scoped>
.schedule-demo-modal {
  display: flex;
  flex-direction: column;
  height: 100%;
  overflow: hidden;
}

.modal-tab-panels {
  flex: 1;
  min-height: 0;
  display: flex;
  flex-direction: column;
}

.tab-panel--kivo {
  flex: 1;
  min-height: 0;
  display: flex;
  flex-direction: column;
  overflow: hidden;
}

.tab-panel--manual {
  flex: 1;
  min-height: 0;
  overflow-y: auto;
  padding: var(--space-medium);
}

.required-star {
  color: var(--r-500);
}

.manual-meeting-form {
  max-width: 520px;
  margin: 0 auto;
  display: flex;
  flex-direction: column;
  gap: var(--space-small);

  .form-group {
    margin-bottom: 0;
  }

  .form-label {
    display: block;
    font-size: var(--font-size-small);
    font-weight: var(--font-weight-medium);
    color: var(--s-800);
    margin-bottom: var(--space-smaller);
  }

  .form-input,
  .form-textarea {
    width: 100%;
    padding: var(--space-small);
    font-size: var(--font-size-default);
    border: 1px solid var(--s-200);
    border-radius: var(--border-radius-normal);
    background: white;
    color: var(--s-900);

    &:focus {
      outline: none;
      border-color: var(--w-500);
      box-shadow: 0 0 0 1px var(--w-200);
    }

    &::placeholder {
      color: var(--s-400);
    }

    &.input-error {
      border-color: var(--r-500);
    }
  }

  .form-textarea {
    resize: vertical;
    min-height: 72px;
  }

  .error-message {
    display: block;
    margin-top: var(--space-micro);
    font-size: var(--font-size-mini);
    color: var(--r-500);
  }

  /* vue-multiselect: multiselect-wrap--medium uses fixed h-12 + nowrap flex — unwrap for many labels */
  .manual-labels-field {
    ::v-deep .multiselect-wrap--medium .multiselect {
      height: auto;
      min-height: 3rem;
    }

    ::v-deep .multiselect-wrap--medium .multiselect__tags {
      display: flex;
      flex-wrap: wrap;
      align-items: flex-start;
      align-content: flex-start;
      gap: 4px;
      min-height: 3rem;
      height: auto;
      overflow: visible;
      padding-top: 6px;
      padding-bottom: 6px;
    }

    ::v-deep .multiselect-wrap--medium .multiselect__tags-wrap {
      display: flex;
      flex-wrap: wrap;
      flex: 1 1 100%;
      min-width: 0;
      max-width: 100%;
      gap: 4px;
      align-items: center;
    }

    ::v-deep .multiselect-wrap--medium .multiselect__tag {
      flex-shrink: 0;
      margin-top: 0;
      margin-bottom: 0;
    }

    /*
     * Do not override .multiselect__input width — vue-multiselect uses inline width:0
     * when closed (multiple + searchable + tags) so the native input placeholder does not
     * show next to chips. Our previous flex/width rules broke that and duplicated "Select one…".
     */
    ::v-deep .multiselect-wrap--medium .multiselect:not(.multiselect--active) {
      .multiselect__input::placeholder {
        color: transparent;
        opacity: 0;
      }
    }
  }

  .manual-form-actions {
    display: flex;
    justify-content: flex-end;
    gap: var(--space-small);
    flex-shrink: 0;
  }
}

// Header (title + tabs inline — matches settings/labels/list tab styling)
.modal-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: var(--space-normal) var(--space-medium);
  border-bottom: 1px solid var(--s-75);
  flex-shrink: 0;
  gap: var(--space-normal);

  .modal-header-left {
    display: flex;
    flex-wrap: wrap;
    align-items: baseline;
    gap: 12px 20px;
    flex: 1;
    min-width: 0;
  }

  .header-title {
    font-size: var(--font-size-large);
    font-weight: var(--font-weight-bold);
    color: var(--s-900);
    margin: 0;
    line-height: 1.25;
    padding: 0 0 5px 0;
    flex-shrink: 0;
    /* Mirror tab underline strip so title + tabs share the same vertical rhythm */
    border-bottom: 3px solid transparent;
    box-sizing: border-box;
  }

  .schedule-modal-tabs {
    display: inline-flex;
    flex-wrap: wrap;
    gap: 12px;
    align-items: baseline;
  }

  .tab-button {
    display: inline-flex;
    align-items: baseline;
    justify-content: center;
    min-width: auto;
    text-align: center;
    padding: 6px 12px 5px 12px;
    margin: 0;
    border: none;
    border-radius: 0;
    /* Reserve underline space on every tab so switching active state does not shift text */
    border-bottom: 3px solid transparent;
    box-sizing: border-box;
    cursor: pointer;
    background-color: transparent;
    color: #151718;
    transition: background-color 0.2s ease, color 0.2s ease,
      border-bottom-color 0.2s ease;
    line-height: 1.25;
    font-size: var(--font-size-small);
    font-weight: 600;
    font-family: inherit;

    &:hover {
      background-color: #f1f3f5;
    }

    &.active {
      background-color: transparent;
      color: #40518a;
      border-bottom-color: #40518a;
    }
  }

  .header-actions {
    display: flex;
    align-items: center;
    flex-shrink: 0;
    gap: var(--space-small);
  }

  .close-btn {
    display: flex;
    align-items: center;
    justify-content: center;
    width: 32px;
    height: 32px;
    border: none;
    background: transparent;
    border-radius: var(--border-radius-normal);
    color: var(--s-600);
    cursor: pointer;
    transition: all 0.15s ease;

    &:hover {
      background: var(--s-50);
      color: var(--s-900);
    }
  }
}

// Loading State
.loading-state {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: var(--space-jumbo);
  flex: 1;

  .loading-text {
    margin-top: var(--space-normal);
    color: var(--s-600);
  }
}

// Empty State
.no-events-state {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: var(--space-jumbo);
  flex: 1;

  .empty-icon {
    color: var(--s-400);
    margin-bottom: var(--space-normal);
  }

  .empty-text {
    color: var(--s-600);
    margin-bottom: var(--space-normal);
    text-align: center;
  }

  .empty-actions {
    display: flex;
    gap: var(--space-small);
  }
}

// Main Content Layout
.schedule-content {
  display: grid;
  grid-template-columns: 240px 1fr 220px;
  flex: 1;
  overflow: hidden;
  min-width: 0;
  min-height: 0;
}

// Booking View - Full width 2-panel layout
.booking-view {
  display: grid;
  grid-template-columns: 320px 1fr;
  flex: 1;
  overflow: hidden;
  min-width: 0;
  min-height: 0;
}

.booking-summary-panel {
  padding: var(--space-large);
  border-right: 1px solid var(--s-75);
  overflow-y: auto;

  .summary-event-name {
    font-size: var(--font-size-large);
    font-weight: var(--font-weight-bold);
    color: var(--s-900);
    margin: 0 0 var(--space-normal) 0;
    padding-bottom: var(--space-normal);
    border-bottom: 1px solid var(--s-100);
  }

  .summary-info-card {
    padding: var(--space-normal);
    border: 1px solid var(--s-100);
    border-radius: var(--border-radius-normal);
    margin-bottom: var(--space-small);
    background: white;

    .info-row {
      display: flex;
      align-items: center;
      gap: var(--space-smaller);
      color: var(--s-700);
      font-size: var(--font-size-default);
    }

    .info-label {
      font-weight: var(--font-weight-medium);
      color: var(--s-800);
      margin-bottom: var(--space-micro);
    }

    .info-value {
      color: var(--s-600);
      font-size: var(--font-size-small);
    }
  }
}

.booking-form-panel {
  padding: var(--space-large);
  overflow-y: auto;

  .form-row {
    display: grid;
    grid-template-columns: 1fr 1fr;
    gap: var(--space-normal);
    margin-bottom: var(--space-normal);
  }

  .form-group {
    margin-bottom: var(--space-normal);

    .form-label {
      display: block;
      font-size: var(--font-size-small);
      font-weight: var(--font-weight-medium);
      color: var(--s-800);
      margin-bottom: var(--space-smaller);
    }

    .form-input {
      width: 100%;
      padding: var(--space-small) var(--space-small);
      font-size: var(--font-size-default);
      border: 1px solid var(--s-200);
      border-radius: var(--border-radius-normal);
      background: white;
      color: var(--s-900);

      &:focus {
        outline: none;
        border-color: var(--w-500);
        box-shadow: 0 0 0 1px var(--w-200);
      }

      &:disabled {
        background: var(--s-50);
        color: var(--s-600);
        cursor: not-allowed;
      }

      &::placeholder {
        color: var(--s-400);
      }

      &.input-error {
        border-color: var(--r-500);

        &:focus {
          border-color: var(--r-500);
          box-shadow: 0 0 0 1px var(--r-200);
        }
      }
    }

    .error-message {
      display: block;
      margin-top: var(--space-micro);
      font-size: var(--font-size-mini);
      color: var(--r-500);
    }

    .location-option {
      display: inline-flex;
      align-items: center;
      gap: var(--space-smaller);
      padding: var(--space-small) var(--space-normal);
      border: 1px solid var(--s-200);
      border-radius: var(--border-radius-normal);
      font-size: var(--font-size-small);
      color: var(--s-700);
      cursor: pointer;

      &.selected {
        border-color: var(--w-500);
        background: var(--w-25);
        color: var(--w-600);
      }

      .radio-dot {
        width: 16px;
        height: 16px;
        border: 2px solid var(--s-300);
        border-radius: 50%;
        position: relative;

        &::after {
          content: '';
          position: absolute;
          top: 50%;
          left: 50%;
          transform: translate(-50%, -50%);
          width: 8px;
          height: 8px;
          border-radius: 50%;
          background: transparent;
        }
      }

      &.selected .radio-dot {
        border-color: var(--w-500);

        &::after {
          background: var(--w-500);
        }
      }
    }

    .add-guest-btn {
      margin-top: var(--space-smaller);
      padding: var(--space-small) var(--space-normal);
      font-size: var(--font-size-small);
      border: 1px solid var(--s-200);
      border-radius: var(--border-radius-normal);
      background: var(--s-50);
      color: var(--s-700);
      cursor: pointer;
      transition: all 0.15s ease;

      &:hover {
        background: var(--s-100);
        border-color: var(--s-300);
      }
    }

    .guests-list {
      display: flex;
      flex-wrap: wrap;
      gap: var(--space-smaller);
      margin-top: var(--space-small);

      .guest-tag {
        display: inline-flex;
        align-items: center;
        gap: var(--space-micro);
        padding: var(--space-micro) var(--space-smaller);
        background: var(--w-50);
        border: 1px solid var(--w-200);
        border-radius: var(--border-radius-small);
        font-size: var(--font-size-mini);
        color: var(--w-700);

        .remove-guest {
          display: flex;
          align-items: center;
          justify-content: center;
          padding: 0;
          border: none;
          background: transparent;
          color: var(--w-600);
          cursor: pointer;

          &:hover {
            color: var(--r-500);
          }
        }
      }
    }
  }

  .form-actions {
    display: flex;
    justify-content: flex-end;
    gap: var(--space-small);
    padding-top: var(--space-normal);
    border-top: 1px solid var(--s-75);
    margin-top: var(--space-normal);
  }
}

// Left Panel - Event Types
.event-types-panel {
  padding: var(--space-normal);
  border-right: 1px solid var(--s-75);
  overflow-y: auto;
  overflow-x: visible;

  .event-type-card {
    padding: var(--space-normal);
    border: 1px solid var(--s-100);
    border-radius: var(--border-radius-normal);
    margin-bottom: var(--space-small);
    cursor: pointer;
    transition: all 0.15s ease;

    &:hover {
      border-color: var(--w-300);
      background: var(--w-25);
    }

    &.selected {
      border-color: var(--w-500);
      background: var(--w-50);
    }

    .event-name {
      font-size: var(--font-size-default);
      font-weight: var(--font-weight-bold);
      color: var(--s-900);
      margin: 0 0 var(--space-smaller) 0;
    }

    .event-duration {
      display: inline-flex;
      align-items: center;
      gap: var(--space-micro);
      font-size: var(--font-size-small);
      color: var(--s-600);
      line-height: 1;

      .clock-icon {
        flex-shrink: 0;
        vertical-align: middle;
        color: var(--s-500);
      }

      span {
        line-height: 14px;
      }
    }

    .timezone-section {
      margin-top: var(--space-small);
      padding-top: var(--space-small);
      border-top: 1px solid var(--s-100);
      position: relative;

      .timezone-label {
        display: block;
        font-size: var(--font-size-mini);
        color: var(--s-600);
        margin-bottom: var(--space-micro);
      }

      .timezone-select {
        width: 100%;
        padding: 8px 10px;
        font-size: var(--font-size-mini);
        border: 1px solid var(--s-200);
        border-radius: var(--border-radius-normal);
        background-color: white;
        color: var(--s-800);
        cursor: pointer;
        line-height: 1.3;
        -webkit-appearance: none;
        -moz-appearance: none;
        appearance: none;
        background-image: none;

        &:focus {
          outline: none;
          border-color: var(--w-500);
          box-shadow: 0 0 0 2px var(--w-100);
        }

        &:hover {
          border-color: var(--s-300);
        }

        option {
          padding: 8px 12px;
          font-size: var(--font-size-small);
          color: var(--s-800);
          background-color: white;
          line-height: 1.5;

          &:checked {
            background-color: var(--w-50);
          }

          &:hover {
            background-color: var(--s-50);
          }
        }
      }
    }
  }
}

// Center Panel - Calendar
.calendar-panel {
  padding: var(--space-normal);
  display: flex;
  flex-direction: column;

  .calendar-header {
    display: flex;
    align-items: center;
    justify-content: space-between;
    margin-bottom: var(--space-normal);

    .month-title {
      font-size: var(--font-size-default);
      font-weight: var(--font-weight-bold);
      color: var(--s-900);
      margin: 0;
    }

    .nav-btn {
      display: flex;
      align-items: center;
      justify-content: center;
      width: 32px;
      height: 32px;
      border: none;
      background: transparent;
      border-radius: var(--border-radius-normal);
      color: var(--s-600);
      cursor: pointer;
      transition: all 0.15s ease;

      &:hover:not(:disabled) {
        background: var(--s-50);
        color: var(--s-900);
      }

      &:disabled {
        opacity: 0.4;
        cursor: not-allowed;
      }
    }
  }

  .calendar-grid {
    .weekdays {
      display: grid;
      grid-template-columns: repeat(7, 1fr);
      gap: 2px;
      margin-bottom: var(--space-smaller);

      .weekday {
        text-align: center;
        font-size: var(--font-size-mini);
        font-weight: var(--font-weight-medium);
        color: var(--s-500);
        padding: var(--space-smaller) 0;
      }
    }

    .days {
      display: grid;
      grid-template-columns: repeat(7, 1fr);
      gap: 4px;
    }
  }

  .calendar-day {
    aspect-ratio: 1;
    display: flex;
    align-items: center;
    justify-content: center;
    border-radius: 50%;
    font-size: var(--font-size-small);
    cursor: default;
    transition: all 0.15s ease;
    color: var(--s-600);
    width: 32px;
    height: 32px;
    margin: auto;
    background: transparent;

    &.empty {
      cursor: default;
      background: transparent !important;
      border: none !important;
    }

    &.past:not(.empty) {
      color: var(--s-300);
      cursor: not-allowed;
    }

    &.today:not(.selected):not(.empty) {
      border: 1px solid var(--s-400);
      color: var(--s-900);
      font-weight: var(--font-weight-medium);
    }

    &.available:not(.selected):not(.today):not(.empty) {
      background: var(--w-500);
      color: white;
      font-weight: var(--font-weight-medium);
      cursor: pointer;

      &:hover {
        background: var(--w-600);
      }
    }

    &.available.today:not(.selected):not(.empty) {
      background: var(--w-100);
      border-color: var(--w-500);
      color: var(--w-700);
      cursor: pointer;

      &:hover {
        background: var(--w-200);
      }
    }

    &.selected:not(.empty) {
      background: #40518a;
      color: white;
      font-weight: var(--font-weight-bold);
      border: 2px solid #2e3a63;
      box-shadow: 0 0 0 2px rgba(64, 81, 138, 0.3);
    }
  }

  .loading-slots {
    display: flex;
    align-items: center;
    justify-content: center;
    gap: var(--space-small);
    padding: var(--space-normal);
    color: var(--s-600);
    font-size: var(--font-size-small);
  }
}

// Right Panel - Time Slots
.slots-panel {
  padding: var(--space-normal);
  border-left: 1px solid var(--s-75);
  display: flex;
  flex-direction: column;
  overflow: hidden;

  .slots-header {
    display: flex;
    justify-content: space-between;
    align-items: center;
    margin-bottom: var(--space-normal);
    flex-shrink: 0;

    .selected-date-title {
      font-size: var(--font-size-default);
      font-weight: var(--font-weight-bold);
      color: var(--s-900);
      margin: 0;
    }

    .time-format-toggle {
      display: flex;
      border-radius: var(--border-radius-normal);
      overflow: hidden;

      .toggle-btn {
        padding: var(--space-micro) var(--space-small);
        border: 1px solid var(--s-200);
        background: white;
        font-size: var(--font-size-mini);
        cursor: pointer;
        transition: all 0.15s ease;

        &:first-child {
          border-radius: var(--border-radius-normal) 0 0
            var(--border-radius-normal);
          border-right: none;
        }

        &:last-child {
          border-radius: 0 var(--border-radius-normal)
            var(--border-radius-normal) 0;
        }

        &.active {
          background: var(--w-500);
          color: white;
          border-color: var(--w-500);
        }
      }
    }
  }

  .no-date-selected,
  .no-slots {
    display: flex;
    align-items: center;
    justify-content: center;
    flex: 1;
    color: var(--s-500);
    font-size: var(--font-size-small);
    text-align: center;
  }

  .slots-list {
    display: flex;
    flex-direction: column;
    gap: var(--space-smaller);
    overflow-y: auto;
    flex: 1;
    padding-right: var(--space-smaller);

    .slot-btn {
      padding: var(--space-small) var(--space-normal);
      border: 1px solid var(--s-200);
      border-radius: var(--border-radius-normal);
      background: white;
      font-size: var(--font-size-small);
      color: var(--s-900);
      cursor: pointer;
      transition: all 0.15s ease;
      text-align: center;

      &:hover {
        border-color: var(--w-500);
        background: var(--w-25);
      }

      &.selected {
        background: var(--w-500);
        color: white;
        border-color: var(--w-500);
      }
    }
  }

  .confirm-section {
    margin-top: var(--space-normal);
    padding-top: var(--space-normal);
    border-top: 1px solid var(--s-75);
    flex-shrink: 0;

    .confirm-btn {
      width: 100%;
    }
  }
}

// Dark Mode
.dark {
  .modal-header {
    .tab-button {
      color: var(--s-300);

      &:hover {
        color: var(--s-100);
        background-color: var(--s-800);
      }

      &.active {
        color: #93a4e8;
        border-bottom-color: #5b6dad;
      }
    }
  }

  .manual-meeting-form {
    .form-label {
      color: var(--s-200);
    }

    .form-input,
    .form-textarea {
      background: var(--s-800);
      border-color: var(--s-600);
      color: var(--s-100);
    }

  }

  .modal-header {
    border-bottom-color: var(--s-700);

    .header-title {
      color: var(--s-100);
    }

    .close-btn {
      color: var(--s-400);

      &:hover {
        background: var(--s-800);
        color: var(--s-100);
      }
    }
  }

  .event-types-panel {
    border-right-color: var(--s-700);

    .event-type-card {
      border-color: var(--s-700);

      &:hover {
        border-color: var(--w-600);
        background: var(--s-800);
      }

      &.selected {
        border-color: var(--w-500);
        background: var(--s-800);
      }

      .event-name {
        color: var(--s-100);
      }

      .timezone-section {
        border-top-color: var(--s-700);

        .timezone-select {
          background: var(--s-800);
          border-color: var(--s-600);
          color: var(--s-100);
        }
      }
    }
  }

  .calendar-panel {
    .calendar-header .month-title {
      color: var(--s-100);
    }

    .calendar-day {
      color: var(--s-500);

      &.available:not(.selected) {
        background: var(--w-900);
        color: var(--w-300);

        &:hover {
          background: var(--w-800);
        }
      }
    }
  }

  .slots-panel {
    border-left-color: var(--s-700);

    .slots-header .selected-date-title {
      color: var(--s-100);
    }

    .time-format-toggle .toggle-btn {
      background: var(--s-800);
      border-color: var(--s-600);
      color: var(--s-300);
    }

    .slots-list .slot-btn {
      background: var(--s-800);
      border-color: var(--s-600);
      color: var(--s-100);

      &:hover {
        border-color: var(--w-500);
      }
    }

    .confirm-section {
      border-top-color: var(--s-700);
    }
  }
}

// Responsive - Medium screens
@media (max-width: 900px) {
  .schedule-content {
    grid-template-columns: minmax(180px, 200px) minmax(250px, 1fr) minmax(
        160px,
        180px
      );
  }

  .event-types-panel .event-type-card {
    padding: var(--space-small);

    .timezone-section .timezone-select {
      font-size: var(--font-size-mini);
    }
  }

  .slots-panel .slots-list .slot-btn {
    padding: var(--space-smaller) var(--space-small);
    font-size: var(--font-size-mini);
  }
}

// Responsive - Small screens
@media (max-width: 700px) {
  .schedule-demo-modal-wrapper {
    ::v-deep .modal-container {
      width: 98%;
      max-width: none;
    }
  }

  .schedule-content {
    grid-template-columns: 1fr;
    grid-template-rows: auto 1fr auto;
  }

  .event-types-panel {
    border-right: none;
    border-bottom: 1px solid var(--s-75);
    display: flex;
    gap: var(--space-small);
    overflow-x: auto;
    padding: var(--space-small);
    max-height: 120px;

    .event-type-card {
      flex-shrink: 0;
      min-width: 150px;
      margin-bottom: 0;
    }
  }

  .calendar-panel {
    min-height: 300px;
  }

  .slots-panel {
    border-left: none;
    border-top: 1px solid var(--s-75);
    max-height: 200px;

    .slots-list {
      flex-direction: row;
      flex-wrap: wrap;
      gap: var(--space-smaller);

      .slot-btn {
        flex: 0 0 auto;
      }
    }
  }
}
</style>
